'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { sendShipmentStatusEmail, sendDeliveryMilestoneEmail, sendCustomsClearanceEmail } from '@/lib/emailService';

interface Shipment {
  id: string;
  trackingNumber: string;
  customer: string;
  origin: string;
  destination: string;
  status: string;
  assignedTo: string | null;
  priority: 'low' | 'medium' | 'high';
  createdDate: string;
}

const mockShipments: Shipment[] = [
  {
    id: '1',
    trackingNumber: 'TRK-2026-001234',
    customer: 'John Doe',
    origin: 'New York, USA',
    destination: 'London, UK',
    status: 'In Transit',
    assignedTo: 'Driver A',
    priority: 'high',
    createdDate: '2026-02-01',
  },
  {
    id: '2',
    trackingNumber: 'TRK-2026-005678',
    customer: 'Sarah Johnson',
    origin: 'Los Angeles, USA',
    destination: 'Tokyo, Japan',
    status: 'Out for Delivery',
    assignedTo: 'Driver B',
    priority: 'medium',
    createdDate: '2026-01-28',
  },
  {
    id: '3',
    trackingNumber: 'TRK-2026-009012',
    customer: 'Michael Chen',
    origin: 'Chicago, USA',
    destination: 'Sydney, Australia',
    status: 'Pending Pickup',
    assignedTo: null,
    priority: 'high',
    createdDate: '2026-02-05',
  },
  {
    id: '4',
    trackingNumber: 'TRK-2026-003456',
    customer: 'Emily Brown',
    origin: 'Miami, USA',
    destination: 'Paris, France',
    status: 'Customs Clearance',
    assignedTo: 'Driver C',
    priority: 'low',
    createdDate: '2026-02-03',
  },
];

const drivers = [
  { id: '1', name: 'Driver A - John Smith' },
  { id: '2', name: 'Driver B - Maria Garcia' },
  { id: '3', name: 'Driver C - David Lee' },
  { id: '4', name: 'Driver D - Sarah Wilson' },
];

const statuses = [
  'Pending Pickup',
  'In Transit',
  'Customs Clearance',
  'Out for Delivery',
  'Delivered',
  'On Hold',
];

export default function ShipmentManagement() {
  const [shipments, setShipments] = useState<Shipment[]>(mockShipments);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedShipment, setSelectedShipment] = useState<Shipment | null>(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);

  const filteredShipments = shipments.filter((shipment) => {
    const matchesStatus = filterStatus === 'all' || shipment.status === filterStatus;
    const matchesPriority = filterPriority === 'all' || shipment.priority === filterPriority;
    const matchesSearch =
      searchQuery === '' ||
      shipment.trackingNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
      shipment.customer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesPriority && matchesSearch;
  });

  const handleAssign = (shipmentId: string, driverId: string) => {
    const driverName = drivers.find((d) => d.id === driverId)?.name || null;
    setShipments(
      shipments.map((s) => (s.id === shipmentId ? { ...s, assignedTo: driverName } : s))
    );
    setShowAssignModal(false);
    setSelectedShipment(null);
  };

  const handleStatusUpdate = async (shipmentId: string, newStatus: string) => {
    const shipment = shipments.find(s => s.id === shipmentId);
    
    setShipments(
      shipments.map((s) => (s.id === shipmentId ? { ...s, status: newStatus } : s))
    );
    setShowStatusModal(false);
    setSelectedShipment(null);

    // Send automated email notifications based on status
    if (shipment && shipment.customer) {
      const recipientEmail = `${shipment.customer.toLowerCase().replace(/\s+/g, '.')}@example.com`;
      
      // Send status update email
      await sendShipmentStatusEmail(
        recipientEmail,
        shipment.trackingNumber,
        newStatus,
        shipment.destination,
        shipment.estimatedDelivery,
        `Your shipment status has been updated to ${newStatus}`
      );

      // Send milestone-specific emails
      if (newStatus === 'Out for Delivery') {
        await sendDeliveryMilestoneEmail(
          recipientEmail,
          shipment.trackingNumber,
          'Out for Delivery',
          shipment.destination,
          'Package will be delivered today'
        );
      } else if (newStatus === 'Delivered') {
        await sendDeliveryMilestoneEmail(
          recipientEmail,
          shipment.trackingNumber,
          'Delivered',
          shipment.destination,
          'Thank you for using our service'
        );
      } else if (newStatus === 'Customs Clearance') {
        await sendCustomsClearanceEmail(
          recipientEmail,
          shipment.trackingNumber,
          shipment.destination,
          'INTL-CUSTOMS-001'
        );
      }
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'text-destructive bg-destructive/10';
      case 'medium':
        return 'text-accent bg-accent/10';
      case 'low':
        return 'text-muted-foreground bg-muted';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Delivered':
        return 'text-success bg-success/10';
      case 'In Transit':
        return 'text-primary bg-primary/10';
      case 'Out for Delivery':
        return 'text-secondary bg-secondary/10';
      case 'On Hold':
        return 'text-destructive bg-destructive/10';
      default:
        return 'text-accent bg-accent/10';
    }
  };

  return (
    <div className="space-y-6">
      {/* Filters and Search */}
      <div className="bg-card rounded-xl p-6 shadow-card border border-border">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Search
            </label>
            <div className="relative">
              <Icon
                name="MagnifyingGlassIcon"
                size={20}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Tracking # or Customer"
                className="w-full pl-10 pr-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Status
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
            >
              <option value="all">All Statuses</option>
              {statuses.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Priority
            </label>
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
            >
              <option value="all">All Priorities</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setFilterStatus('all');
                setFilterPriority('all');
                setSearchQuery('');
              }}
              className="w-full px-4 py-2 bg-muted text-foreground rounded-lg text-sm font-medium hover:bg-muted/80 transition-colors"
            >
              Reset Filters
            </button>
          </div>
        </div>
      </div>

      {/* Shipments Table */}
      <div className="bg-card rounded-xl shadow-card border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left py-4 px-6 text-sm font-semibold text-foreground">
                  Tracking Number
                </th>
                <th className="text-left py-4 px-6 text-sm font-semibold text-foreground">
                  Customer
                </th>
                <th className="text-left py-4 px-6 text-sm font-semibold text-foreground">
                  Route
                </th>
                <th className="text-left py-4 px-6 text-sm font-semibold text-foreground">
                  Status
                </th>
                <th className="text-left py-4 px-6 text-sm font-semibold text-foreground">
                  Priority
                </th>
                <th className="text-left py-4 px-6 text-sm font-semibold text-foreground">
                  Assigned To
                </th>
                <th className="text-left py-4 px-6 text-sm font-semibold text-foreground">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredShipments.map((shipment) => (
                <tr
                  key={shipment.id}
                  className="border-t border-border hover:bg-muted/30 transition-colors"
                >
                  <td className="py-4 px-6">
                    <span className="text-sm font-mono font-medium text-primary">
                      {shipment.trackingNumber}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <span className="text-sm text-foreground">{shipment.customer}</span>
                  </td>
                  <td className="py-4 px-6">
                    <div className="text-sm text-muted-foreground">
                      <div>{shipment.origin}</div>
                      <div className="flex items-center gap-1 my-1">
                        <Icon name="ArrowDownIcon" size={12} className="text-primary" />
                      </div>
                      <div>{shipment.destination}</div>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                        shipment.status
                      )}`}
                    >
                      {shipment.status}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium uppercase ${getPriorityColor(
                        shipment.priority
                      )}`}
                    >
                      {shipment.priority}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <span className="text-sm text-foreground">
                      {shipment.assignedTo || (
                        <span className="text-muted-foreground italic">Unassigned</span>
                      )}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setSelectedShipment(shipment);
                          setShowAssignModal(true);
                        }}
                        className="p-2 hover:bg-primary/10 rounded-lg transition-colors"
                        title="Assign Driver"
                      >
                        <Icon name="UserPlusIcon" size={18} className="text-primary" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedShipment(shipment);
                          setShowStatusModal(true);
                        }}
                        className="p-2 hover:bg-secondary/10 rounded-lg transition-colors"
                        title="Update Status"
                      >
                        <Icon name="PencilSquareIcon" size={18} className="text-secondary" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredShipments.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <Icon name="InboxIcon" size={48} className="mx-auto mb-4" />
            <p>No shipments found matching your filters</p>
          </div>
        )}
      </div>

      {/* Assign Driver Modal */}
      {showAssignModal && selectedShipment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl p-6 max-w-md w-full shadow-xl border border-border">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-foreground">Assign Driver</h3>
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedShipment(null);
                }}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <Icon name="XMarkIcon" size={20} className="text-muted-foreground" />
              </button>
            </div>
            <div className="mb-6">
              <p className="text-sm text-muted-foreground mb-2">Tracking Number</p>
              <p className="text-lg font-mono font-medium text-primary">
                {selectedShipment.trackingNumber}
              </p>
            </div>
            <div className="space-y-3">
              {drivers.map((driver) => (
                <button
                  key={driver.id}
                  onClick={() => handleAssign(selectedShipment.id, driver.id)}
                  className="w-full flex items-center gap-3 p-4 border border-border rounded-lg hover:bg-muted transition-colors text-left"
                >
                  <Icon name="UserIcon" size={20} className="text-primary" />
                  <span className="text-sm font-medium text-foreground">{driver.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Update Status Modal */}
      {showStatusModal && selectedShipment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl p-6 max-w-md w-full shadow-xl border border-border">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-foreground">Update Status</h3>
              <button
                onClick={() => {
                  setShowStatusModal(false);
                  setSelectedShipment(null);
                }}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <Icon name="XMarkIcon" size={20} className="text-muted-foreground" />
              </button>
            </div>
            <div className="mb-6">
              <p className="text-sm text-muted-foreground mb-2">Tracking Number</p>
              <p className="text-lg font-mono font-medium text-primary">
                {selectedShipment.trackingNumber}
              </p>
              <p className="text-sm text-muted-foreground mt-2">Current Status</p>
              <p className="text-sm font-medium text-foreground">{selectedShipment.status}</p>
            </div>
            <div className="space-y-3">
              {statuses.map((status) => (
                <button
                  key={status}
                  onClick={() => handleStatusUpdate(selectedShipment.id, status)}
                  className={`w-full flex items-center gap-3 p-4 border border-border rounded-lg hover:bg-muted transition-colors text-left ${
                    selectedShipment.status === status ? 'bg-primary/5 border-primary' : ''
                  }`}
                >
                  <span className="text-sm font-medium text-foreground">{status}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}